import { UnidadeMedidaSelectModel } from 'components/form/field/select/UnidadeMedidaSelectField'
import { TipoFrequencia, TurnoEnum, UnidadeMedidaTempoEnum } from 'graphql/types.generated'
import { formatHoursAsDays } from 'util/date/formatDate'
import { formatNumberAsPtBr, parseFraction } from 'util/number'
import { parseNumber } from 'util/validation'
import { isEmpty } from 'util/validation/Util'

import { enumToStringTurno, PosologiaLabelParams, TipoDoseEnum } from '../model-prescricao'

interface FrequenciaParams {
  tipoFrequencia: TipoFrequencia
  frequenciaDose?: number
  quantidadePeriodoFrequencia?: number
  unidadeMedidaTempoFrequencia?: UnidadeMedidaTempoEnum
  turno?: TurnoEnum
}

interface DoseParams {
  intervaloDose?: number
  doseUnica?: boolean
  doseText?: string
  doseManhaText?: string
  doseTardeText?: string
  doseNoiteText?: string
}

type FrequenciaReceitaParams = FrequenciaParams & DoseParams

export const formatPosologia = (params: PosologiaLabelParams): string | null => {
  const {
    tipoFrequencia,
    frequenciaDose,
    intervaloDose,
    qtDose,
    tipoDose,
    unidadeMedida,
    doseDw,
    qtDoseManha,
    qtDoseTarde,
    qtDoseNoite,
  } = params

  const isFrequenciaOuIntervaloVazio =
    (tipoFrequencia === TipoFrequencia.FREQUENCIA && !frequenciaDose) ||
    (tipoFrequencia === TipoFrequencia.INTERVALO && !intervaloDose)

  if (isFrequenciaOuIntervaloVazio && !qtDose) {
    return 'Nenhuma informação adicionada'
  }

  const doseText = formatDoseReceitaLabel(unidadeMedida, qtDose, doseDw)
  const doseManhaText = unidadeMedida ? formatDoseReceitaLabel(unidadeMedida, qtDoseManha, null) : qtDoseManha
  const doseTardeText = unidadeMedida ? formatDoseReceitaLabel(unidadeMedida, qtDoseTarde, null) : qtDoseTarde
  const doseNoiteText = unidadeMedida ? formatDoseReceitaLabel(unidadeMedida, qtDoseNoite, null) : qtDoseNoite

  return formatFrequenciaLabel({
    tipoFrequencia,
    intervaloDose,
    frequenciaDose,
    quantidadePeriodoFrequencia: params.quantidadePeriodoFrequencia,
    unidadeMedidaTempoFrequencia: params.unidadeMedidaTempoFrequencia,
    turno: params.turno,
    doseUnica: tipoDose === TipoDoseEnum.UNICA,
    doseText,
    doseManhaText,
    doseTardeText,
    doseNoiteText,
  })
}

const appendCommaToDoseText = (doseText: string | null | undefined, textToAppend: string | number): string => {
  return `${doseText ?? ''}${doseText && textToAppend ? ',' : ''}`
}

export function formatFrequenciaLabel(params: FrequenciaReceitaParams): string | null {
  const {
    doseUnica,
    doseText,
    quantidadePeriodoFrequencia,
    unidadeMedidaTempoFrequencia,
    tipoFrequencia,
    intervaloDose,
    frequenciaDose,
    turno,
  } = params

  if (doseUnica) {
    const doseUnicaText = 'dose única'
    return `${appendCommaToDoseText(doseText, doseUnicaText)} ${doseUnicaText}`
  }

  const frequenciaText = formatFrequenciaPeriodoText(
    quantidadePeriodoFrequencia,
    unidadeMedidaTempoFrequencia,
    tipoFrequencia
  )

  const appendComma = (text: string | number) => appendCommaToDoseText(doseText, text)

  switch (tipoFrequencia) {
    case TipoFrequencia.INTERVALO:
      return formatIntervaloText(intervaloDose, appendComma)
    case TipoFrequencia.FREQUENCIA:
      return formatFrequenciaText(frequenciaDose, frequenciaText, appendComma)
    case TipoFrequencia.TURNO:
      return formatTurnoText(turno, frequenciaText, appendComma)
    case TipoFrequencia.FRACIONADO:
      return formatFracionadoText(params, frequenciaText)
    default:
      return null
  }
}

function formatIntervaloText(
  intervaloDose: number | null | undefined,
  formatter: (val: string | number) => string
): string {
  const textoIntervalo = intervaloDose ? `a cada ${formatHoursAsDays(parseNumber(intervaloDose))}` : ''
  return `${formatter(intervaloDose ?? '')} ${textoIntervalo}`
}

function formatVezesText(frequenciaDose: number): string {
  if (!frequenciaDose) return ''
  const qtd = parseNumber(frequenciaDose)
  return qtd === 1 ? `${frequenciaDose} vez` : `${frequenciaDose} vezes`
}

function formatFrequenciaText(
  frequenciaDose: number,
  frequenciaText: string,
  formatter: (val: string | number) => string
): string {
  const vezesText = formatVezesText(frequenciaDose)
  return `${formatter(frequenciaDose ?? '')} ${vezesText} ${frequenciaText}`
}

function formatTurnoText(
  turno: TurnoEnum | null | undefined,
  frequenciaText: string | null | undefined,
  formatter: (val: string | number) => string
): string {
  const turnoText = turno ? `pela ${enumToStringTurno[turno]}` : ''
  return `${formatter(turno ?? '')} ${turnoText} ${frequenciaText ?? ''}`
}

function buildDosesFracionadas(params: FrequenciaReceitaParams): ReadonlyArray<string> {
  const { doseManhaText, doseTardeText, doseNoiteText } = params
  const doses: string[] = []

  if (doseManhaText) doses.push(`${doseManhaText} pela manhã`)
  if (doseTardeText) doses.push(`${doseTardeText} pela tarde`)
  if (doseNoiteText) doses.push(`${doseNoiteText} pela noite`)

  return doses
}

function formatFracionadoText(params: FrequenciaReceitaParams, frequenciaText: string): string {
  const { doseText } = params
  const doseFracionadaArray = buildDosesFracionadas(params)
  const temFracionamento = !isEmpty(doseFracionadaArray)

  if (temFracionamento) {
    const doseFracionadaText = doseFracionadaArray.join(', ').replaceLastOccurrence(', ', ' e')
    const conector = doseText ? ', sendo: ' : ''
    return `${doseText ?? ''} ${frequenciaText ?? ''}${conector}${doseFracionadaText}`
  }

  return `${doseText ?? ''} ${frequenciaText ?? ''}`
}

function formatFrequenciaPeriodoText(qtde: number, unidade: UnidadeMedidaTempoEnum, tipo: TipoFrequencia): string {
  if (qtde && unidade) {
    return `${formatUnidadeTempo(parseNumber(qtde), unidade, tipo)}`
  }
  return ''
}

export function formatDoseReceitaLabel(
  unidadeMedida: UnidadeMedidaSelectModel | null | undefined,
  qtDose: string | null | undefined,
  doseDw: string | null | undefined
): string | null {
  if (doseDw) return doseDw.toLocaleLowerCase()

  if (!unidadeMedida || !qtDose) return null

  const qtDoseText = formatNumberAsPtBr(qtDose)
  const parsedDose = parseFraction(qtDoseText)

  let unidadeMedidaText = unidadeMedida.nome
  if (parsedDose >= 2) {
    unidadeMedidaText = unidadeMedida.nomePlural
  }

  return `${qtDoseText} ${unidadeMedidaText.toLowerCase()}`
}

const formatUnidadeTempoDias = (value: number, tipoFrequencia: TipoFrequencia): string => {
  if (value !== 1) {
    return `a cada ${value} dias`
  }
  if (tipoFrequencia === TipoFrequencia.TURNO) return ''
  if (tipoFrequencia === TipoFrequencia.FRACIONADO) return 'a cada 1 dia'

  return 'ao dia'
}

export function formatUnidadeTempo(
  value: number,
  unidade: UnidadeMedidaTempoEnum,
  tipoFrequencia: TipoFrequencia
): string | null {
  switch (unidade) {
    case UnidadeMedidaTempoEnum.DIAS:
      return formatUnidadeTempoDias(value, tipoFrequencia)
    case UnidadeMedidaTempoEnum.SEMANAS:
      return `a cada ${value} ${value === 1 ? 'semana' : 'semanas'}`
    case UnidadeMedidaTempoEnum.MESES:
      return `a cada ${value} ${value === 1 ? 'mês' : 'meses'}`
    default:
      return null
  }
}

export function formatUnidadeMedidaText(qtDose: string, unidadeMedida: UnidadeMedidaSelectModel): string {
  if (!unidadeMedida) return ''

  const isPlural = parseNumber(qtDose) >= 2
  const text = isPlural ? unidadeMedida.nomePlural : unidadeMedida.nome
  return text.toLowerCase()
}
